import { useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { panel } from './panel';
import { useFocusTrap } from './useFocusTrap';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  side?: 'right' | 'left';
  width?: string;
  /** Extra classes for the scrolling body, e.g. `p-0` for a full-bleed navigation list. */
  bodyClassName?: string;
}

/** Side panel (detail views, mobile navigation). Same a11y contract as Dialog. */
export function Drawer({ open, onClose, title, subtitle, children, footer, side = 'right', width = 'max-w-xl', bodyClassName }: DrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useFocusTrap(panelRef, open, onClose);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex">
      <div className="anim-fade-in absolute inset-0 bg-ink/35" aria-hidden onClick={onClose} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(
          'relative flex h-full w-full flex-col border-line bg-surface shadow-[0_0_48px_-16px_rgba(24,24,27,0.35)] focus:outline-none',
          width,
          side === 'right' ? 'anim-slide-in-right ml-auto border-l' : 'anim-slide-in-left mr-auto border-r',
        )}
      >
        <div className={panel.header}>
          <div className="min-w-0">
            <h2 id={titleId} className="truncate text-base leading-snug font-semibold" title={typeof title === 'string' ? title : undefined}>
              {title}
            </h2>
            {subtitle && <div className="mt-1.5 text-[13px] leading-normal text-muted">{subtitle}</div>}
          </div>
          <button type="button" onClick={onClose} className={panel.close} aria-label="Đóng">
            <X className="size-5" />
          </button>
        </div>
        <div className={cn(panel.body, bodyClassName)}>{children}</div>
        {footer && <div className={panel.footer}>{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
